import React, { useState, useEffect } from 'react';
import { Card, Progress, Typography, Space, Button, Alert, Spin, Timeline } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const TemplateMatchProgress = ({ taskId, onComplete, onError, onDownload }) => {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('running');
  const [currentStep, setCurrentStep] = useState('');
  const [message, setMessage] = useState('');
  const [dataCount, setDataCount] = useState(0);
  const [resultId, setResultId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taskStartTime, setTaskStartTime] = useState(Date.now());
  const [startTime, setStartTime] = useState(null);

  // 轮询获取进度
  useEffect(() => {
    if (!taskId) return;

    let retryCount = 0;
    const maxRetries = 3;

    const pollProgress = async () => {
      try {
        // 检查任务是否超时（30分钟）
        const elapsedTime = Date.now() - taskStartTime;
        const maxTaskTime = 30 * 60 * 1000; // 30分钟
        
        if (elapsedTime > maxTaskTime && status === 'running') {
          console.warn('任务超时，停止轮询');
          onError && onError('任务处理时间过长，请检查服务器状态或重试');
          return;
        }
        
        const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
        if (!token) {
          console.error('没有找到认证token');
          return;
        }

        const response = await fetch(`/api/v1/data-processing/template-match-progress/${taskId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('模板匹配进度更新:', data); // 调试日志
          console.log('任务状态:', data.status, '进度:', data.progress, '步骤:', data.current_step);
          
          setProgress(data.progress || 0);
          setStatus(data.status || 'running');
          setCurrentStep(data.current_step || '');
          setMessage(data.message || '');
          setDataCount(data.data_count || 0);
          setStartTime(data.start_time);
          setResultId(data.result_id);
          setLoading(false);
          retryCount = 0; // 重置重试计数

          // 检查是否完成
          if (data.status === 'completed') {
            console.log('模板匹配完成:', data.result_id);
            onComplete && onComplete(data.result_id);
          } else if (data.status === 'failed' || data.status === 'cancelled') {
            console.log('模板匹配失败:', data.message);
            onError && onError(data.message || '任务失败');
          }
        } else if (response.status === 404) {
          console.error('任务不存在:', taskId);
          console.error('响应内容:', await response.text());
          onError && onError('任务不存在');
        } else if (response.status === 403) {
          console.error('无权访问此任务');
          onError && onError('无权访问此任务');
        } else {
          console.error('获取进度失败，状态码:', response.status);
          console.error('响应内容:', await response.text());
          retryCount++;
          if (retryCount >= maxRetries) {
            onError && onError('获取进度失败，请刷新页面重试');
          }
        }
      } catch (error) {
        console.error('获取进度失败:', error);
        retryCount++;
        if (retryCount >= maxRetries) {
          onError && onError('网络错误，请检查连接');
        }
      }
    };

    // 立即获取一次
    pollProgress();

    // 设置轮询间隔
    const interval = setInterval(pollProgress, 2000); // 每2秒轮询一次

    return () => clearInterval(interval);
  }, [taskId, onComplete, onError]);

  // 获取状态图标
  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'cancelled':
        return <CloseCircleOutlined style={{ color: '#faad14' }} />;
      default:
        return <LoadingOutlined style={{ color: '#1890ff' }} />;
    }
  };

  // 获取状态颜色
  const getStatusColor = () => {
    switch (status) {
      case 'completed':
        return '#52c41a';
      case 'failed':
        return '#ff4d4f';
      case 'cancelled':
        return '#faad14';
      default:
        return '#1890ff';
    }
  };

  // 获取步骤描述
  const getStepDescription = (step) => {
    const stepMap = {
      'init': '初始化处理',
      'copy': '复制模板文件',
      'open': '打开Excel工作簿',
      'write': '写入数据',
      'save': '保存文件',
      'complete': '处理完成',
      'error': '处理失败',
      'cancelled': '已取消'
    };
    return stepMap[step] || step;
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text>正在获取任务状态...</Text>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* 标题和状态 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Title level={4} style={{ margin: 0 }}>
            {getStatusIcon()} 模板匹配进度
          </Title>
          <Text type="secondary">
            任务ID: {taskId?.slice(0, 8)}...
          </Text>
        </div>

        {/* 进度条 */}
        <div>
          <Progress
            percent={progress}
            status={status === 'failed' ? 'exception' : status === 'completed' ? 'success' : 'active'}
            strokeColor={getStatusColor()}
            format={(percent) => `${percent}%`}
          />
        </div>

        {/* 状态信息 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Text strong>状态:</Text>
            <Text style={{ color: getStatusColor() }}>
              {status === 'running' ? '处理中' : 
               status === 'completed' ? '已完成' : 
               status === 'failed' ? '失败' : 
               status === 'cancelled' ? '已取消' : status}
            </Text>
          </Space>
          <Space>
            <Text strong>数据量:</Text>
            <Text>{dataCount} 行</Text>
          </Space>
        </div>

        {/* 当前步骤 */}
        <div>
          <Text strong>当前步骤: </Text>
          <Text>{getStepDescription(currentStep)}</Text>
        </div>

        {/* 消息 */}
        {message && (
          <Alert
            message={message}
            type={status === 'failed' ? 'error' : status === 'completed' ? 'success' : 'info'}
            showIcon
          />
        )}

        {/* 时间信息 */}
        {startTime && (
          <div>
            <Space>
              <ClockCircleOutlined />
              <Text type="secondary">
                开始时间: {new Date(startTime).toLocaleString()}
              </Text>
            </Space>
          </div>
        )}

        {/* 完成后的结果 */}
        {status === 'completed' && resultId && (
          <Alert
            message="模板匹配完成！"
            description={`结果文件ID: ${resultId}`}
            type="success"
            showIcon
            action={
              <Button 
                size="small" 
                type="primary"
                onClick={() => onDownload && onDownload(resultId)}
              >
                下载结果
              </Button>
            }
          />
        )}
      </Space>
    </Card>
  );
};

export default TemplateMatchProgress;
